import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { plural } from '../utils';
import SyncHistoryTable from '../components/SyncHistoryTable';
import SyncResultBanner, { SYNC_METRICS } from '../components/SyncResultBanner';

const TABS = [
  { id: 'workday', label: 'Workday Sync' },
  { id: 'hexnode', label: 'Hexnode Sync' },
  { id: 'ninja',   label: 'Ninja RMM Sync' },
  { id: 'verizon', label: 'Verizon Import' },
];

const { processed, created, updated, skipped, errored } = SYNC_METRICS;

// One-line summary of the counts a sync reports back. Tolerates a missing or
// null `stats` — a sync can fail partway and return without it.
function countsLine(stats, extra = []) {
  const s = stats || {};
  return [
    `${s.processed || 0} processed`,
    `${s.created || 0} created`,
    `${s.updated || 0} updated`,
    `${s.skipped || 0} skipped`,
    ...extra,
  ].join(', ');
}

// The preview panel reads a dozen fields straight off one response and formats
// most of them, so a single absent number threw mid-render — `.toLocaleString()`
// on undefined, `.toFixed()` on NaN — and took the page down with it. The counts
// were defended one tile at a time and the termination row, the part that
// actually pulls devices off people, was not among them.
//
// Normalizing here rather than at each use means the JSX can format freely, and
// a field the API stops sending degrades to 0 instead of to a blank screen.
function normalizePlan(plan) {
  const p = plan || {};
  const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
  return {
    ...p,
    incoming: num(p.incoming),
    toCreate: num(p.toCreate),
    toUpdate: num(p.toUpdate),
    toReactivate: num(p.toReactivate),
    toTerminate: num(p.toTerminate),
    toTerminateExplicit: num(p.toTerminateExplicit),
    toTerminateAbsent: num(p.toTerminateAbsent),
    terminationRatio: num(p.terminationRatio),
    activeInDb: num(p.activeInDb),
    minimumEntries: num(p.minimumEntries),
    threshold: num(p.threshold),
    unknownLocations: Array.isArray(p.unknownLocations) ? p.unknownLocations : [],
  };
}

// Verizon Enterprise Center exports a CSV with quoted fields, so a plain
// split(',') would break on any value containing a comma.
function parseVerizonCsv(text) {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length === 0) return [];
  const headers = lines[0].split(',').map(h => h.trim());

  return lines.slice(1).map(line => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQuotes = !inQuotes; }
      else if (line[i] === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else { current += line[i]; }
    }
    values.push(current.trim());

    const row = {};
    headers.forEach((h, i) => { row[h] = (values[i] || '').trim(); });
    return row;
  }).filter(r => r['Wireless number']);
}

export default function Integrations() {
  const { withAuth } = useAuth();
  const [tab, setTab] = useState('workday');
  const [loading, setLoading] = useState(true);

  const [workdayHistory, setWorkdayHistory] = useState([]);
  const [workdaySyncing, setWorkdaySyncing] = useState(false);
  const [workdayResult, setWorkdayResult] = useState(null);
  // An uploaded report is previewed before it is applied. `pending` holds the
  // parsed payload and the plan the API says it would carry out, so the numbers
  // on screen are the numbers the run will act on — same planner, same call.
  const [workdayPending, setWorkdayPending] = useState(null);
  const [workdayPreviewing, setWorkdayPreviewing] = useState(false);
  const [workdayForce, setWorkdayForce] = useState(false);

  const [hexnodeHistory, setHexnodeHistory] = useState([]);
  const [hexnodeSyncing, setHexnodeSyncing] = useState(false);
  const [hexnodeResult, setHexnodeResult] = useState(null);
  const [hexnodeEnriching, setHexnodeEnriching] = useState(false);
  const [hexnodeEnrichResult, setHexnodeEnrichResult] = useState(null);

  const [ninjaHistory, setNinjaHistory] = useState([]);
  const [ninjaSyncing, setNinjaSyncing] = useState(false);
  const [ninjaResult, setNinjaResult] = useState(null);

  const [verizonHistory, setVerizonHistory] = useState([]);
  const [verizonImporting, setVerizonImporting] = useState(false);
  const [verizonResult, setVerizonResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        await Promise.all([
          api.getSyncStatus({ source: 'workday' }).then(r => setWorkdayHistory(r.data || [])),
          api.getHexnodeSyncHistory().then(r => setHexnodeHistory(r || [])),
          api.getNinjaSyncHistory().then(r => setNinjaHistory(r || [])),
          api.getVerizonHistory().then(r => setVerizonHistory(r || [])),
        ]);
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [withAuth]);

  useEffect(() => { load(); }, [load]);

  async function handleWorkdayAutoSync() {
    setWorkdaySyncing(true);
    setWorkdayResult(null);
    try {
      await withAuth(async () => {
        setWorkdayResult(await api.runWorkdayAutoSync());
        const res = await api.getSyncStatus({ source: 'workday' });
        setWorkdayHistory(res.data || []);
      });
    } catch (err) {
      setWorkdayResult({ error: err.message || 'Auto-sync failed' });
    } finally {
      setWorkdaySyncing(false);
    }
  }

  // Upload no longer syncs. It previews, and waits.
  //
  // The API has had a preview endpoint all along, computing exactly the counts
  // that decide whether a run is safe. Nothing in the UI called it, so the only
  // way to see them was a hand-built curl with a copied bearer token — which
  // meant in practice nobody looked before running a sync that can terminate
  // employees and pull their devices back.
  async function handleWorkdayUpload(file) {
    if (!file) return;
    setWorkdayPreviewing(true);
    setWorkdayResult(null);
    setWorkdayPending(null);
    setWorkdayForce(false);
    try {
      const json = JSON.parse(await file.text());
      const plan = await withAuth(() => api.previewWorkdaySync(json));
      setWorkdayPending({ json, plan: normalizePlan(plan), name: file.name });
    } catch (err) {
      setWorkdayResult({ error: err.message });
    } finally {
      setWorkdayPreviewing(false);
    }
  }

  async function handleWorkdayConfirm() {
    if (!workdayPending) return;
    setWorkdaySyncing(true);
    try {
      await withAuth(async () => {
        const payload = workdayForce
          ? { ...workdayPending.json, force: true }
          : workdayPending.json;
        setWorkdayResult(await api.runWorkdaySync(payload));
        const res = await api.getSyncStatus({ source: 'workday' });
        setWorkdayHistory(res.data || []);
      });
      setWorkdayPending(null);
    } catch (err) {
      setWorkdayResult({ error: err.message });
    } finally {
      setWorkdaySyncing(false);
    }
  }

  async function handleHexnodeSync() {
    setHexnodeSyncing(true);
    setHexnodeResult(null);
    try {
      await withAuth(async () => {
        setHexnodeResult(await api.runHexnodeSync());
        setHexnodeHistory(await api.getHexnodeSyncHistory() || []);
      });
    } catch (err) {
      setHexnodeResult({ error: err.message });
    } finally {
      setHexnodeSyncing(false);
    }
  }

  async function handleHexnodeEnrich() {
    setHexnodeEnriching(true);
    setHexnodeEnrichResult(null);
    try {
      await withAuth(async () => {
        setHexnodeEnrichResult(await api.runHexnodeEnrich());
      });
    } catch (err) {
      setHexnodeEnrichResult({ error: err.message });
    } finally {
      setHexnodeEnriching(false);
    }
  }

  async function handleNinjaSync() {
    setNinjaSyncing(true);
    setNinjaResult(null);
    try {
      await withAuth(async () => {
        setNinjaResult(await api.runNinjaSync());
        setNinjaHistory(await api.getNinjaSyncHistory() || []);
      });
    } catch (err) {
      setNinjaResult({ error: err.message });
    } finally {
      setNinjaSyncing(false);
    }
  }

  async function handleVerizonUpload(file) {
    if (!file) return;
    setVerizonImporting(true);
    setVerizonResult(null);
    try {
      const rows = parseVerizonCsv(await file.text());
      await withAuth(async () => {
        setVerizonResult(await api.runVerizonImport(rows));
        setVerizonHistory(await api.getVerizonHistory() || []);
      });
    } catch (err) {
      setVerizonResult({ error: err.message });
    } finally {
      setVerizonImporting(false);
    }
  }

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>Integrations</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>
            Run and review the syncs that feed employee and device records
          </p>
        </div>
        <button className="btn btn-sm" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, padding: '0 28px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 14px', background: 'none', border: 'none',
            borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === t.id ? 'var(--text-0)' : 'var(--text-2)',
            fontSize: 14, fontWeight: tab === t.id ? 500 : 400,
            cursor: 'pointer', marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '20px 28px' }}>
        {loading ? <div className="empty-state"><div className="spinner" /></div> : (
          <>
            {tab === 'workday' && (
              <div style={{ display: 'grid', gap: 20 }}>
                <div className="card" style={{ padding: 24 }}>
                  <h3 style={{ marginBottom: 8 }}>Run Workday Sync</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 8 }}>
                    Fetch the latest employee data directly from Workday, or upload a manually exported HCM Manpower Report JSON.
                  </p>
                  {/* The two routes differ in what can be checked beforehand.
                      Preview needs the payload in hand, and the direct fetch
                      only has it server-side — so that one leans on the
                      server's own threshold check instead. */}
                  <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 20 }}>
                    An uploaded report is previewed before anything changes. A direct fetch applies
                    immediately, but the server still refuses any run that would terminate an
                    implausible share of staff.
                  </p>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleWorkdayAutoSync}
                      disabled={workdaySyncing}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                      {workdaySyncing ? <><div className="spinner" /> Syncing…</> : '🔄 Run Sync Now'}
                    </button>
                    <span style={{ color: 'var(--text-2)', fontSize: 13 }}>or</span>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)', background: 'var(--bg-2)',
                      cursor: (workdaySyncing || workdayPreviewing) ? 'not-allowed' : 'pointer',
                      fontSize: 14, fontWeight: 500,
                    }}>
                      {workdayPreviewing ? <><div className="spinner" /> Reading…</> : '📄 Upload Report JSON'}
                      <input
                        type="file" accept=".json" style={{ display: 'none' }}
                        // Clearing the value is what lets the same filename be picked twice —
                        // the natural move after fixing a rejected file.
                        onChange={e => { handleWorkdayUpload(e.target.files[0]); e.target.value = ''; }}
                        disabled={workdaySyncing || workdayPreviewing}
                      />
                    </label>
                  </div>


                  {workdayPending && (() => {
                    const p = workdayPending.plan;
                    const blocked = p.exceedsThreshold && !workdayForce;
                    return (
                      <div style={{
                        marginTop: 20, padding: 16, borderRadius: 'var(--radius-sm)',
                        background: 'var(--bg-2)',
                        border: `1px solid ${p.exceedsThreshold ? 'var(--red)' : 'var(--border)'}`,
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          Review before applying — {workdayPending.name}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
                          Nothing has changed yet. These are the counts the sync would act on.
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                          {[
                            { label: 'In report', value: p.incoming },
                            { label: 'New records', value: p.toCreate },
                            { label: 'Updated', value: p.toUpdate },
                            { label: 'Reactivated', value: p.toReactivate },
                          ].map(m => (
                            <div key={m.label} style={{ background: 'var(--bg-1)', padding: '8px 12px', borderRadius: 'var(--radius-sm)' }}>
                              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{m.label}</div>
                              <div style={{ fontWeight: 600 }}>{m.value.toLocaleString()}</div>
                            </div>
                          ))}
                        </div>

                        {/* Terminations get their own row: they are the only part
                            of a sync that pulls devices back off people. */}
                        <div style={{
                          marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                          background: p.toTerminate > 0 ? 'var(--yellow-dim)' : 'var(--bg-1)',
                          border: `1px solid ${p.toTerminate > 0 ? 'var(--yellow)' : 'var(--border)'}`,
                        }}>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                            {p.toTerminate.toLocaleString()} {plural(p.toTerminate, 'termination')}
                            <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                              {' '}· {(p.terminationRatio * 100).toFixed(1)}% of {p.activeInDb.toLocaleString()} active
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                            {p.toTerminateExplicit} marked terminated in Workday ·{' '}
                            {p.toTerminateAbsent} missing from the report entirely
                          </div>
                          {p.toTerminate > 0 && (
                            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
                              Their assigned devices will be flagged for return.
                            </div>
                          )}
                        </div>

                        {p.unknownLocations.length > 0 && (
                          <div style={{ fontSize: 13, color: 'var(--yellow)', marginTop: 10 }}>
                            ⚠ {p.unknownLocations.length} unmatched {plural(p.unknownLocations.length, 'location')} —
                            those employees sync without a site: {p.unknownLocations.slice(0, 5).join(', ')}
                            {p.unknownLocations.length > 5 && ` +${p.unknownLocations.length - 5} more`}
                          </div>
                        )}

                        {p.belowMinimum && (
                          <div style={{ color: 'var(--red)', fontSize: 14, marginTop: 10 }}>
                            ✗ Only {p.incoming} entries — below the {p.minimumEntries} minimum. The API will refuse this.
                          </div>
                        )}

                        {p.exceedsThreshold && (
                          <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)' }}>
                            <div style={{ color: 'var(--red)', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                              Above the {(p.threshold * 100).toFixed(0)}% termination threshold — the API will refuse this run.
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                              <input type="checkbox" checked={workdayForce} onChange={e => setWorkdayForce(e.target.checked)} style={{ width: 'auto' }} />
                              Override — I have checked these numbers and they are correct (IT admin only)
                            </label>
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                          <button
                            className="btn btn-primary"
                            onClick={handleWorkdayConfirm}
                            disabled={workdaySyncing || blocked || p.belowMinimum}
                          >
                            {workdaySyncing ? 'Applying…' : 'Apply sync'}
                          </button>
                          <button className="btn" onClick={() => { setWorkdayPending(null); setWorkdayForce(false); }} disabled={workdaySyncing}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  <SyncResultBanner result={workdayResult} style={{ marginTop: 20, padding: 16 }}>
                    <div style={{ fontWeight: 600, color: 'var(--green)', marginBottom: 8 }}>✓ Sync Complete</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                      {[
                        { label: 'Processed',  value: workdayResult?.stats?.processed },
                        { label: 'Created',    value: workdayResult?.stats?.created },
                        { label: 'Updated',    value: workdayResult?.stats?.updated },
                        { label: 'Terminated', value: workdayResult?.stats?.terminated },
                        { label: 'Errors',     value: workdayResult?.stats?.errored },
                        { label: 'Duration',   value: workdayResult?.durationMs ? `${workdayResult.durationMs}ms` : 'N/A' },
                      ].map(s => (
                        <div key={s.label} style={{ background: 'var(--bg-2)', padding: '8px 12px', borderRadius: 'var(--radius-sm)' }}>
                          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{s.label}</div>
                          <div style={{ fontWeight: 600 }}>{s.value ?? 0}</div>
                        </div>
                      ))}
                    </div>
                    {workdayResult?.terminatedEmployees?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 500, marginBottom: 6, color: 'var(--yellow)' }}>
                          ⚠ {workdayResult.terminatedEmployees.length} termination{workdayResult.terminatedEmployees.length !== 1 ? 's' : ''} detected — see Reports → Pending Returns
                        </div>
                        {workdayResult.terminatedEmployees.map(e => (
                          <div key={e.id} style={{ fontSize: 13, color: 'var(--text-1)', marginBottom: 2 }}>
                            {e.name} {e.email ? `(${e.email})` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                  </SyncResultBanner>
                </div>

                <SyncHistoryTable
                  rows={workdayHistory}
                  metrics={[processed, created, updated, errored]}
                  emptyMessage="No sync history"
                />
              </div>
            )}

            {tab === 'hexnode' && (
              <div style={{ display: 'grid', gap: 20 }}>
                <div className="card" style={{ padding: 24 }}>
                  <h3 style={{ marginBottom: 8 }}>Sync from Hexnode MDM</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 16 }}>
                    Pull all enrolled devices from Hexnode and create or update asset records. Matches devices to employees by name.
                  </p>

                  <SyncResultBanner result={hexnodeResult} style={{ marginBottom: 16 }}>
                    ✓ Sync complete — {countsLine(hexnodeResult?.stats)}
                  </SyncResultBanner>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={handleHexnodeSync} disabled={hexnodeSyncing}>
                      {hexnodeSyncing ? 'Syncing…' : '⟳ Run Hexnode Sync'}
                    </button>
                    <button
                      className="btn"
                      onClick={handleHexnodeEnrich}
                      disabled={hexnodeEnriching || hexnodeSyncing}
                      title="Fetch IMEI, phone number, location and storage details for each device"
                    >
                      {hexnodeEnriching ? 'Enriching…' : '◎ Enrich Device Details'}
                    </button>
                  </div>

                  <SyncResultBanner result={hexnodeEnrichResult} style={{ marginTop: 12 }}>
                    ✓ Enriched {hexnodeEnrichResult?.stats?.enriched || 0} devices, {hexnodeEnrichResult?.stats?.errors || 0} errors
                  </SyncResultBanner>
                </div>

                <SyncHistoryTable
                  rows={hexnodeHistory}
                  metrics={[processed, created, updated, skipped]}
                />
              </div>
            )}

            {tab === 'ninja' && (
              <div style={{ display: 'grid', gap: 20 }}>
                <div className="card" style={{ padding: 24 }}>
                  <h3 style={{ marginBottom: 8 }}>Sync from Ninja RMM</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 16 }}>
                    Pull all managed devices from NinjaOne and create or update laptop, desktop, and server asset records. Matches devices to employees by assigned owner or last logged-in user.
                  </p>

                  <SyncResultBanner result={ninjaResult} style={{ marginBottom: 16 }}>
                    ✓ Sync complete — {countsLine(ninjaResult?.stats)}
                  </SyncResultBanner>

                  <button className="btn btn-primary" onClick={handleNinjaSync} disabled={ninjaSyncing}>
                    {ninjaSyncing ? 'Syncing…' : '⟳ Run Ninja RMM Sync'}
                  </button>
                </div>

                <SyncHistoryTable
                  rows={ninjaHistory}
                  metrics={[processed, created, updated, skipped]}
                />
              </div>
            )}

            {tab === 'verizon' && (
              <div style={{ display: 'grid', gap: 20 }}>
                <div className="card" style={{ padding: 24 }}>
                  <h3 style={{ marginBottom: 8 }}>Import Verizon Device Report</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 4 }}>
                    Upload the CSV exported from Verizon Enterprise Center. Phones and tablets will be matched to existing Hexnode assets by IMEI or phone number and enriched with carrier data. Radios (Feature Phones) will be created as new assets.
                  </p>
                  <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 16 }}>
                    Export from: Verizon Enterprise Center → Devices → Export
                  </p>

                  <SyncResultBanner result={verizonResult} style={{ marginBottom: 16 }}>
                    ✓ Import complete — {countsLine(verizonResult?.stats, [`${verizonResult?.stats?.errors || 0} errors`])}
                  </SyncResultBanner>

                  <label style={{ display: 'inline-block' }}>
                    <input
                      type="file"
                      accept=".csv"
                      style={{ display: 'none' }}
                      onChange={e => { handleVerizonUpload(e.target.files[0]); e.target.value = ''; }}
                      disabled={verizonImporting}
                    />
                    <span className="btn btn-primary" style={{ cursor: verizonImporting ? 'not-allowed' : 'pointer', opacity: verizonImporting ? 0.6 : 1 }}>
                      {verizonImporting ? 'Importing…' : '↑ Upload Verizon CSV'}
                    </span>
                  </label>
                </div>

                <SyncHistoryTable
                  title="Import History"
                  rows={verizonHistory}
                  metrics={[processed, created, updated, skipped, errored]}
                  emptyMessage="No import history yet"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
